import { useCallback, useEffect, useRef, useState } from "react"
import { useRouter } from "next/router"

import type WaveSurfer from "wavesurfer.js"
import HoverPlugin from "wavesurfer.js/dist/plugins/hover.esm.js"
import type { RegionParams } from "wavesurfer.js/dist/plugins/regions.js"

import { formatSeekTime } from "@/lib/audio-player-utils"
import { attachSwappedStereoPlayback } from "@/components/shared/audio/swappedStereoPlayback"
import {
  PEAKS_LOAD_TIMEOUT_MS,
  type WavesurferPeaks,
} from "@/hooks/usePrecomputedPeaks"

export function isAbortedAudioLoad(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error.name === "AbortError" ||
      error.message.includes("The operation was aborted"))
  )
}

export function loadWaveformSource(
  wavesurfer: WaveSurfer,
  signedUrl: string,
  precomputedPeaks: WavesurferPeaks | null | undefined,
  audioDuration: number | null | undefined,
): Promise<void> {
  return precomputedPeaks
    ? wavesurfer.load(signedUrl, precomputedPeaks, audioDuration ?? undefined)
    : wavesurfer.load(signedUrl)
}

export function pauseForRouteChange(
  wavesurfer: WaveSurfer | null,
  shallow: boolean,
) {
  if (shallow || !wavesurfer) {
    return
  }
  try {
    wavesurfer.pause()
  } catch {
    // Wavesurfer can already be tearing down during route transitions.
  }
}

export function useAudioWaveformLifecycle({
  audioDuration,
  currentTime,
  isPlaying,
  isReady,
  loadablePrecomputedPeaks,
  precomputedPeaksPending = false,
  onIsPlayingChange,
  onReady,
  onTimeUpdate,
  onSeek,
  playRange,
  playbackRate,
  recordingKey,
  regions,
  signedUrl,
  seekRequestId,
  time,
  togglePlayPauseRequestId,
  waveformVisible = true,
  wavesurfer,
}: {
  audioDuration?: number | null
  currentTime: number
  isPlaying: boolean
  isReady: boolean
  loadablePrecomputedPeaks?: WavesurferPeaks | null
  precomputedPeaksPending?: boolean
  onIsPlayingChange?: (isPlaying: boolean) => void
  onReady?: () => void
  onTimeUpdate?: (currentTimeSeconds: number) => void
  onSeek?: (time: number, seekTarget?: number) => void
  playRange?: { start: number; end: number; requestId: number }
  playbackRate: number
  recordingKey?: string
  regions?: RegionParams[]
  signedUrl?: string
  seekRequestId?: number
  time?: number
  togglePlayPauseRequestId?: number
  waveformVisible?: boolean
  wavesurfer: WaveSurfer | null
}) {
  const router = useRouter()
  const [loadError, setLoadError] = useState<Error | null>(null)
  const loadOptionsRef = useRef({
    audioDuration,
    precomputedPeaks: loadablePrecomputedPeaks,
  })
  const loadedSourceRef = useRef<{
    wavesurfer: WaveSurfer
    signedUrl: string
  } | null>(null)
  const lastPlayRangeIdRef = useRef<number | undefined>(undefined)
  const lastTogglePlayPauseRequestIdRef = useRef<number | undefined>(
    togglePlayPauseRequestId,
  )

  const sourceKey = recordingKey ?? signedUrl
  const pendingSeekRef = useRef<{ seconds: number; sourceKey?: string } | null>(
    null,
  )
  const seekTo = useCallback(
    (seconds: number) => {
      if (!Number.isFinite(seconds)) {
        return
      }
      if (wavesurfer && isReady && wavesurfer.getDuration() > 0) {
        pendingSeekRef.current = null
        const nextTime = Math.max(
          0,
          Math.min(wavesurfer.getDuration(), seconds),
        )
        wavesurfer.setTime(nextTime)
        onSeek?.(wavesurfer.getCurrentTime(), seconds)
      } else {
        pendingSeekRef.current = { seconds, sourceKey }
      }
    },
    [wavesurfer, isReady, sourceKey, onSeek],
  )

  const onPlayPause = useCallback(() => {
    if (!wavesurfer) {
      return
    }
    void wavesurfer.playPause()
    onIsPlayingChange?.(!isPlaying)
  }, [wavesurfer, isPlaying, onIsPlayingChange])

  useEffect(() => {
    if (!wavesurfer || !isReady || !waveformVisible) {
      return
    }
    const hover = wavesurfer.registerPlugin(
      HoverPlugin.create({
        lineColor: "var(--coval-text-muted)",
        labelColor: "var(--coval-text-on-contrast)",
        labelBackground: "var(--coval-contrast)",
        formatTimeCallback: formatSeekTime,
      }),
    )
    return () => hover.destroy()
  }, [wavesurfer, isReady, waveformVisible])

  useEffect(() => {
    const handleRouteChange = (
      _url: string,
      { shallow }: { shallow: boolean },
    ) => {
      pauseForRouteChange(wavesurfer, shallow)
    }
    router.events.on("routeChangeStart", handleRouteChange)
    return () => {
      router.events.off("routeChangeStart", handleRouteChange)
    }
  }, [router.events, wavesurfer])

  useEffect(() => {
    if (togglePlayPauseRequestId == null) {
      return
    }
    if (lastTogglePlayPauseRequestIdRef.current === togglePlayPauseRequestId) {
      return
    }
    lastTogglePlayPauseRequestIdRef.current = togglePlayPauseRequestId
    if (wavesurfer && isReady) {
      onPlayPause()
    }
  }, [togglePlayPauseRequestId, wavesurfer, isReady, onPlayPause])

  useEffect(() => {
    if (isReady && wavesurfer) {
      wavesurfer.setPlaybackRate(playbackRate)
    }
  }, [isReady, wavesurfer, playbackRate, regions])

  // Ordered before the load effect below: the swap has to attach before the element's source is assigned.
  useEffect(() => {
    if (!wavesurfer) {
      return
    }
    return attachSwappedStereoPlayback(wavesurfer.getMediaElement())
  }, [wavesurfer])

  useEffect(() => {
    loadOptionsRef.current = {
      audioDuration,
      precomputedPeaks: loadablePrecomputedPeaks,
    }
  }, [audioDuration, loadablePrecomputedPeaks])

  useEffect(() => {
    if (!signedUrl || !wavesurfer) {
      return
    }
    let isCurrent = true
    const handleError = (error: Error) => {
      if (isCurrent && !isAbortedAudioLoad(error)) {
        setLoadError(error)
      }
    }
    wavesurfer.on("error", handleError)
    const load = () => {
      if (
        loadedSourceRef.current?.wavesurfer === wavesurfer &&
        loadedSourceRef.current.signedUrl === signedUrl
      ) {
        return
      }
      loadedSourceRef.current = { wavesurfer, signedUrl }
      setLoadError(null)
      const loadOptions = loadOptionsRef.current
      void loadWaveformSource(
        wavesurfer,
        signedUrl,
        loadOptions.precomputedPeaks,
        loadOptions.audioDuration,
      ).catch((error: unknown) => {
        handleError(error instanceof Error ? error : new Error(String(error)))
      })
    }
    // Signed URL lookup and peaks fetch share this bounded head start over full decoding.
    const timeout = precomputedPeaksPending
      ? setTimeout(load, PEAKS_LOAD_TIMEOUT_MS)
      : undefined
    if (!precomputedPeaksPending) {
      load()
    }
    return () => {
      isCurrent = false
      clearTimeout(timeout)
      wavesurfer.un("error", handleError)
    }
  }, [signedUrl, wavesurfer, precomputedPeaksPending])

  useEffect(() => {
    if (isReady) {
      onReady?.()
    }
  }, [isReady, onReady])

  useEffect(() => {
    if (isReady && wavesurfer && typeof time === "number") {
      seekTo(time)
    }
  }, [time, seekRequestId, isReady, wavesurfer, seekTo])

  useEffect(() => {
    const pending = pendingSeekRef.current
    if (!pending) {
      return
    }
    if (pending.sourceKey !== sourceKey) {
      pendingSeekRef.current = null
    } else if (isReady && wavesurfer && wavesurfer.getDuration() > 0) {
      seekTo(pending.seconds)
    }
  }, [isReady, wavesurfer, sourceKey, seekTo])

  useEffect(() => {
    if (!playRange || !wavesurfer || !isReady) {
      return
    }
    if (lastPlayRangeIdRef.current === playRange.requestId) {
      return
    }
    lastPlayRangeIdRef.current = playRange.requestId
    wavesurfer.setTime(playRange.start)
    void wavesurfer.play()

    const handleTimeUpdate = (nextTime: number) => {
      if (nextTime >= playRange.end) {
        wavesurfer.pause()
      }
    }
    wavesurfer.on("timeupdate", handleTimeUpdate)
    return () => {
      wavesurfer.un("timeupdate", handleTimeUpdate)
    }
  }, [playRange, wavesurfer, isReady])

  useEffect(() => {
    if (isReady) {
      onTimeUpdate?.(currentTime)
    }
  }, [currentTime, isReady, onTimeUpdate])

  return { loadError, onPlayPause, seekTo }
}
